/**
 * Well Installation Intelligence Panel
 *
 * Recommends WHERE to drill and HOW DEEP based on AI-predicted
 * water table decline. Shows:
 *   - Heatmap of future saturated thickness
 *   - Top 10 recommended locations with depth/longevity/yield
 *   - Avoid zones where sat thickness drops below viability
 *   - Summary stats
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { MapContainer, CircleMarker, Rectangle, Tooltip as LTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { apiClient } from '../../api/client';
import { EsriVectorBasemap } from '@/lib/basemap';

interface WellInstallationPanelProps {
  siteId: string;
  aquifer: string;
  wells: { well_name: string; latitude: number; longitude: number; total_depth_ft?: number | null }[];
  predictionHorizons?: Record<string, Record<string, number>> | null;
  centerLat: number;
  centerLon: number;
  siteAcres?: number;
  demandGpmPerWell?: number;
  propertyBounds?: { north: number; south: number; east: number; west: number } | null;
}

interface Candidate {
  lat: number;
  lon: number;
  current_sat_ft: number;
  future_sat_ft: number;
  current_dtw_ft: number;
  future_dtw_ft: number;
  theis_drawdown_ft: number;
  recommended_depth_ft: number;
  longevity_years: number;
  estimated_yield_gpm: number;
  ground_elevation_ft: number;
  nearest_well_ft: number;
  zone: string;
  score: number;
}

interface InstallationResult {
  candidates: Candidate[];
  heatmap: Candidate[];
  avoid_zones: { lat: number; lon: number; reason: string }[];
  summary: {
    total_grid_points: number;
    viable_locations: number;
    avoid_zones: number;
    target_horizon: string;
    aquifer: string;
    avg_future_sat_ft: number;
    min_future_sat_ft: number;
    max_future_sat_ft: number;
    avg_recommended_depth_ft: number;
    avg_longevity_years: number;
    best_yield_gpm: number;
  };
}

const HORIZON_OPTIONS = ['3mo', '1yr', '2yr', '5yr', '10yr', '20yr'];

const ZONE_COLORS: Record<string, string> = {
  excellent: '#10b981',
  good: '#06b6d4',
  marginal: '#f59e0b',
  avoid: '#ef4444',
};

const ZONE_LABELS: Record<string, string> = {
  excellent: '>100ft sat',
  good: '50-100ft',
  marginal: '20-50ft',
  avoid: '<20ft',
};

export function WellInstallationPanel({ siteId, aquifer, wells, predictionHorizons, centerLat, centerLon, siteAcres = 640, demandGpmPerWell = 0, propertyBounds }: WellInstallationPanelProps) {
  const [result, setResult] = useState<InstallationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [horizon, setHorizon] = useState('20yr');
  const [gridSpacing, setGridSpacing] = useState(500);
  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null);
  const [planningWell, setPlanningWell] = useState(false);
  const [plannedCount, setPlannedCount] = useState(0);

  const planWell = async (candidate: Candidate, index: number) => {
    setPlanningWell(true);
    try {
      await apiClient.post('/phreatic/pump-control/assets', {
        site_id: siteId,
        well_name: `PW-${plannedCount + 1}`,
        latitude: candidate.lat,
        longitude: candidate.lon,
        well_depth_ft: candidate.recommended_depth_ft,
        ground_elevation_ft: candidate.ground_elevation_ft,
        pump_rate_gpm: candidate.estimated_yield_gpm,
        aquifer_name: aquifer,
        status: 'active',
        source: 'well_intelligence',
        recommendation_data: {
          rank: index + 1,
          future_sat_ft: candidate.future_sat_ft,
          longevity_years: candidate.longevity_years,
          theis_drawdown_ft: candidate.theis_drawdown_ft,
          horizon,
          score: candidate.score,
        },
      });
      setPlannedCount(prev => prev + 1);
    } catch (err: any) {
      console.error('Failed to plan well:', err);
    } finally {
      setPlanningWell(false);
    }
  };

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.post('/phreatic/calculator/well-installation', {
        site_id: siteId,
        aquifer,
        target_horizon: horizon,
        grid_spacing_ft: gridSpacing,
        demand_gpm_per_well: demandGpmPerWell,
      });
      setResult(resp.data);
      setSelectedCandidate(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // Re-run when site OR selected aquifer changes — the backend now filters
  // existing wells by aquifer, so the recommendation grid (depth, sat
  // thickness, longevity) is aquifer-specific. Without re-running here, the
  // user would see stale numbers from the previous aquifer.
  useEffect(() => {
    runAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, aquifer]);

  // Heatmap grid visualization data
  const heatmapGrid = useMemo(() => {
    if (!result?.heatmap) return [];
    return result.heatmap.map(p => ({
      ...p,
      color: ZONE_COLORS[p.zone] || '#666',
    }));
  }, [result]);

  // Zone distribution for pie-like bar chart
  const zoneDistribution = useMemo(() => {
    if (!result?.heatmap) return [];
    const counts: Record<string, number> = { excellent: 0, good: 0, marginal: 0, avoid: 0 };
    for (const p of result.heatmap) counts[p.zone] = (counts[p.zone] || 0) + 1;
    return Object.entries(counts).map(([zone, count]) => ({
      zone,
      count,
      label: ZONE_LABELS[zone] || zone,
      color: ZONE_COLORS[zone] || '#666',
    }));
  }, [result]);

  const s = result?.summary;

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-mono-400 uppercase tracking-wider">
            Well Installation Intelligence
          </h3>
          <button
            onClick={runAnalysis}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50"
          >
            {loading ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Target Horizon</label>
            <select
              value={horizon}
              onChange={e => setHorizon(e.target.value)}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
            >
              {HORIZON_OPTIONS.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Grid Spacing (ft)</label>
            <select
              value={gridSpacing}
              onChange={e => setGridSpacing(Number(e.target.value))}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
            >
              <option value={250}>250 ft (fine)</option>
              <option value={500}>500 ft (default)</option>
              <option value={1000}>1000 ft (coarse)</option>
              <option value={2000}>2000 ft (fast)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Aquifer</label>
            <div className="mt-0.5 px-2 py-1 text-xs bg-mono-800/50 border border-mono-700 rounded text-cyan-400">
              {aquifer}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Existing Wells</label>
            <div className="mt-0.5 px-2 py-1 text-xs bg-mono-800/50 border border-mono-700 rounded text-mono-300">
              {wells.length} wells
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-32 bg-mono-900 border border-mono-800 rounded-lg">
          <div className="text-mono-400 text-sm animate-pulse">Running installation intelligence analysis...</div>
        </div>
      )}

      {result && s && (
        <>
          {/* Summary Cards. Yield/GPM intentionally hidden — the underlying
              specific-capacity model uses a flat per-aquifer K and an
              unjustified ×0.5 scaler, so the number isn't trustworthy yet.
              Bring it back once we wire TWDB transmissivity per-region. */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <SummaryCard label="Viable Locations" value={`${s.viable_locations}`} sub={`of ${s.total_grid_points} evaluated`} color="cyan" />
            <SummaryCard label="Avg Future Sat" value={`${s.avg_future_sat_ft} ft`} sub={`at ${s.target_horizon} (${aquifer})`}
              color={s.avg_future_sat_ft < 50 ? 'amber' : 'cyan'} />
            <SummaryCard label="Rec. Drill Depth" value={`${s.avg_recommended_depth_ft} ft`} sub={`avg across top 10 (${aquifer})`} color="cyan" />
            <SummaryCard label="Avg Longevity" value={s.avg_longevity_years > 500 ? 'Stable' : `${s.avg_longevity_years} yr`}
              sub="before cavitation" color={s.avg_longevity_years < 20 ? 'red' : s.avg_longevity_years < 50 ? 'amber' : 'green'} />
            <SummaryCard label="Avoid Zones" value={`${s.avoid_zones}`}
              sub={`<20ft at ${s.target_horizon}`} color={s.avoid_zones > 0 ? 'red' : 'green'} />
          </div>

          {/* Satellite Map with Recommendations */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
              Property — Recommended Well Locations
            </h3>
            <div className="rounded-lg overflow-hidden" style={{ height: 400 }}>
              <MapContainer
                key={`map-${result.candidates.length}-${horizon}`}
                center={[centerLat, centerLon]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <EsriVectorBasemap />
                {/* Property boundary — real bounds if available, else square from acreage */}
                {(() => {
                  let bounds: [[number, number], [number, number]];
                  if (propertyBounds) {
                    bounds = [
                      [propertyBounds.south, propertyBounds.west],
                      [propertyBounds.north, propertyBounds.east],
                    ];
                  } else {
                    const sideFt = Math.sqrt(siteAcres * 43560);
                    const halfLat = (sideFt / 2) / 364000;
                    const halfLon = (sideFt / 2) / (364000 * Math.cos(centerLat * Math.PI / 180));
                    bounds = [
                      [centerLat - halfLat, centerLon - halfLon],
                      [centerLat + halfLat, centerLon + halfLon],
                    ];
                  }
                  return (
                    <Rectangle bounds={bounds} pathOptions={{ color: '#06b6d4', weight: 2, fillOpacity: 0.05, dashArray: '6 4' }} />
                  );
                })()}
                {/* Existing wells — white dots */}
                {wells.map(w => (
                  <CircleMarker
                    key={w.well_name}
                    center={[w.latitude, w.longitude]}
                    radius={4}
                    pathOptions={{ color: '#fff', fillColor: '#fff', fillOpacity: 0.9, weight: 1 }}
                  >
                    <LTooltip direction="top" offset={[0, -6]}>
                      <span style={{ fontSize: 11 }}>{w.well_name} (existing)</span>
                    </LTooltip>
                  </CircleMarker>
                ))}
                {/* Heatmap grid points — colored by zone */}
                {result.heatmap.map((p, i) => (
                  <CircleMarker
                    key={`h-${i}`}
                    center={[p.lat, p.lon]}
                    radius={3}
                    pathOptions={{
                      color: 'transparent',
                      fillColor: ZONE_COLORS[p.zone],
                      fillOpacity: 0.35,
                      weight: 0,
                    }}
                  />
                ))}
                {/* Top 10 candidates — numbered cyan circles */}
                {result.candidates.map((c, i) => (
                  <CircleMarker
                    key={`c-${i}`}
                    center={[c.lat, c.lon]}
                    radius={selectedCandidate === i ? 10 : 7}
                    pathOptions={{
                      color: selectedCandidate === i ? '#fff' : '#06b6d4',
                      fillColor: ZONE_COLORS[c.zone],
                      fillOpacity: 0.8,
                      weight: selectedCandidate === i ? 3 : 2,
                    }}
                    eventHandlers={{ click: () => setSelectedCandidate(selectedCandidate === i ? null : i) }}
                  >
                    <LTooltip direction="top" offset={[0, -8]} permanent={i < 3}>
                      <span style={{ fontSize: 10, fontWeight: 600 }}>
                        #{i + 1} — {c.recommended_depth_ft}ft
                      </span>
                    </LTooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <div className="flex items-center gap-4 mt-2 text-[9px] text-mono-400">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-white border border-mono-600" /> Existing Well
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full border-2 border-cyan-400" style={{ backgroundColor: ZONE_COLORS.excellent }} /> Recommended
              </span>
              {Object.entries(ZONE_COLORS).map(([zone, color]) => (
                <span key={zone} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color, opacity: 0.5 }} />
                  {zone}
                </span>
              ))}
              <span className="flex items-center gap-1">
                <span className="w-4 h-2 border border-cyan-400 border-dashed" style={{ opacity: 0.5 }} /> Property
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Top 10 Recommended Locations */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                Top 10 Recommended Drill Locations
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-mono-400 border-b border-mono-800">
                      <th className="text-left py-1.5 px-1.5">#</th>
                      <th className="text-right py-1.5 px-1.5">Future Sat</th>
                      <th className="text-right py-1.5 px-1.5">Rec. Depth</th>
                      <th className="text-right py-1.5 px-1.5">Longevity</th>
                      <th className="text-right py-1.5 px-1.5">Theis DD</th>
                      <th className="text-center py-1.5 px-1.5">Zone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.candidates.map((c, i) => (
                      <tr
                        key={i}
                        className={`border-b border-mono-800/50 text-mono-300 cursor-pointer transition-colors ${selectedCandidate === i ? 'bg-cyan-500/10' : 'hover:bg-mono-800/50'}`}
                        onClick={() => setSelectedCandidate(selectedCandidate === i ? null : i)}
                      >
                        <td className="py-1.5 px-1.5 font-medium text-mono-400">{i + 1}</td>
                        <td className="text-right py-1.5 px-1.5 font-semibold" style={{ color: ZONE_COLORS[c.zone] }}>
                          {c.future_sat_ft} ft
                        </td>
                        <td className="text-right py-1.5 px-1.5 text-cyan-400 font-semibold">{c.recommended_depth_ft} ft</td>
                        <td className="text-right py-1.5 px-1.5">
                          {c.longevity_years > 500 ? <span className="text-cyan-400">Stable</span> : `${c.longevity_years} yr`}
                        </td>
                        <td className="text-right py-1.5 px-1.5 text-mono-500">{c.theis_drawdown_ft} ft</td>
                        <td className="text-center py-1.5 px-1.5">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: ZONE_COLORS[c.zone] }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Selected candidate detail */}
              {selectedCandidate != null && result.candidates[selectedCandidate] && (
                <div className="mt-3 bg-mono-800/50 rounded-lg p-3 border border-cyan-500/20">
                  {(() => {
                    const c = result.candidates[selectedCandidate];
                    return (
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-cyan-300">Location #{selectedCandidate + 1} Detail</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                          <div className="text-mono-500">Coordinates</div>
                          <div className="text-mono-300">{c.lat.toFixed(5)}, {c.lon.toFixed(5)}</div>
                          <div className="text-mono-500">Ground Elevation</div>
                          <div className="text-mono-300">{c.ground_elevation_ft} ft MSL</div>
                          <div className="text-mono-500">Current DTW</div>
                          <div className="text-mono-300">{c.current_dtw_ft} ft</div>
                          <div className="text-mono-500">Projected DTW ({horizon})</div>
                          <div className="text-mono-300">{c.future_dtw_ft} ft</div>
                          <div className="text-mono-500">Current Sat Thickness</div>
                          <div className="text-mono-300">{c.current_sat_ft} ft</div>
                          <div className="text-mono-500">Future Sat Thickness</div>
                          <div className="text-mono-300 font-semibold" style={{ color: ZONE_COLORS[c.zone] }}>{c.future_sat_ft} ft</div>
                          <div className="text-mono-500">Theis Drawdown (existing pumps)</div>
                          <div className="text-mono-300">{c.theis_drawdown_ft} ft</div>
                          <div className="text-mono-500">Nearest Existing Well</div>
                          <div className="text-mono-300">{c.nearest_well_ft.toLocaleString()} ft</div>
                        </div>
                        <div className="border-t border-mono-700 pt-2 mt-2">
                          <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-1">Recommendation</div>
                          <div className="text-xs text-mono-200">
                            Drill to <span className="text-cyan-400 font-bold">{c.recommended_depth_ft} ft</span> for
                            an estimated <span className="text-cyan-400 font-bold">
                              {c.longevity_years > 500 ? 'indefinite' : `${c.longevity_years} year`}
                            </span> production life.
                          </div>
                          <div className="mt-1 text-[10px] text-mono-600 font-mono">
                            depth = future_DTW({c.future_dtw_ft}ft) + submergence(30ft) + safety(20ft)
                          </div>
                          <button
                            onClick={() => planWell(c, selectedCandidate)}
                            disabled={planningWell}
                            className="mt-2 px-3 py-1.5 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50"
                          >
                            {planningWell ? 'Saving...' : `Plan Well PW-${plannedCount + 1} at This Location`}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Viability Zone Distribution */}
            <div className="space-y-3">
              <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                  Site Viability at {horizon}
                </h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={zoneDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#888', fontSize: 10 }} />
                    <YAxis dataKey="label" type="category" tick={{ fill: '#888', fontSize: 10 }} width={80} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
                      formatter={(val: number) => [`${val} grid points`, 'Count']}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {zoneDistribution.map((d, i) => (
                        <Cell key={i} fill={d.color} fillOpacity={0.7} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-[9px] text-mono-400">
                  {Object.entries(ZONE_COLORS).map(([zone, color]) => (
                    <span key={zone} className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color, opacity: 0.7 }} />
                      {zone.charAt(0).toUpperCase() + zone.slice(1)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Recommended Depth vs Longevity scatter — replaces the
                  Depth-vs-Yield chart while yield is hidden. Future sat
                  thickness encoded as bubble size. */}
              <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                  Depth vs Longevity — Top Locations
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <ScatterChart margin={{ top: 5, right: 10, bottom: 25, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis
                      dataKey="recommended_depth_ft" type="number" name="Depth"
                      tick={{ fill: '#888', fontSize: 10 }}
                      label={{ value: 'Recommended Depth (ft)', position: 'insideBottom', offset: -15, fill: '#666', fontSize: 10 }}
                    />
                    <YAxis
                      dataKey="longevity_years" type="number" name="Longevity"
                      tick={{ fill: '#888', fontSize: 10 }}
                      label={{ value: 'Longevity (yr)', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 10 }}
                    />
                    <ZAxis dataKey="future_sat_ft" range={[40, 200]} name="Sat Thickness" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
                      formatter={(val: number, name: string) => [
                        `${val}${name === 'Depth' ? ' ft' : name === 'Longevity' ? ' yr' : ' ft'}`,
                        name,
                      ]}
                    />
                    <Scatter data={result.candidates} fill="#06b6d4" fillOpacity={0.8}>
                      {result.candidates.map((c, i) => (
                        <Cell key={i} fill={ZONE_COLORS[c.zone]} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* AI Placement Savings Analysis — temporarily hidden. The savings
              math compounds yield (GPM) × FMV per acre-foot, both of which
              ride on the unreliable specific-capacity constant. Bring back
              once yield is driven by TWDB-derived transmissivity. */}

          {/* Avoid Zones */}
          {result.avoid_zones.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">
                Avoid Zones ({result.avoid_zones.length} locations)
              </h3>
              <p className="text-[11px] text-mono-400 mb-2">
                These areas are projected to have less than 20ft of saturated thickness at {horizon},
                making new well installation unviable.
              </p>
              <div className="flex flex-wrap gap-1">
                {result.avoid_zones.slice(0, 20).map((az, i) => (
                  <span key={i} className="text-[9px] text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded">
                    {az.lat.toFixed(4)}, {az.lon.toFixed(4)}
                  </span>
                ))}
                {result.avoid_zones.length > 20 && (
                  <span className="text-[9px] text-mono-500">+{result.avoid_zones.length - 20} more</span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) {
  const colorMap: Record<string, string> = {
    cyan: 'text-cyan-400 border-cyan-500/30',
    green: 'text-cyan-400 border-cyan-500/30',
    amber: 'text-cyan-400 border-cyan-500/30',
    red: 'text-red-400 border-red-500/30',
  };
  const c = colorMap[color] || colorMap.cyan;
  return (
    <div className={`bg-mono-900 border rounded-lg p-2 ${c.split(' ')[1]}`}>
      <div className="text-[9px] text-mono-500 uppercase tracking-wider">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${c.split(' ')[0]}`}>{value}</div>
      <div className="text-[9px] text-mono-600">{sub}</div>
    </div>
  );
}
